<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import SiteHeader from '../components/home/SiteHeader.vue';
import SiteFooter from '../components/home/SiteFooter.vue';
import CatalogCard from '../components/catalog/CatalogCard.vue';
import HeroCarousel from '../components/home/HeroCarousel.vue';
import { platforms } from '../data/games';
import { fetchProducts, type Product } from '../api/catalog';
import mascot from '../assets/home/mascot.webp';

const { t } = useI18n();

const products = ref<Product[]>([]);
const loading = ref(true);
const failed = ref(false);

const carouselItems = computed(() => products.value.slice(0, 20));
const featured = computed(() => products.value.slice(0, 8));

const steps = computed(() => [
  { title: t('home.step1Title'), body: t('home.step1Body'), icon: 'controller' },
  { title: t('home.step2Title'), body: t('home.step2Body'), icon: 'wallet' },
  { title: t('home.step3Title'), body: t('home.step3Body'), icon: 'key' },
]);

onMounted(async () => {
  try {
    products.value = await fetchProducts(20);
  } catch {
    failed.value = true;
  } finally {
    loading.value = false;
  }
});
</script>

<template>
  <div class="relative min-h-screen overflow-hidden text-ink" style="background-color: #0f1012;">
    <!-- Single continuous backdrop for the whole page — no per-section colours or separators -->
    <div class="pointer-events-none absolute inset-0 z-0">
      <div class="absolute left-[-12%] top-[28%] h-[620px] w-[620px] rounded-full bg-[radial-gradient(circle,rgba(240,86,26,0.10),transparent_70%)] blur-3xl"></div>
      <div class="absolute right-[-14%] top-[54%] h-[660px] w-[660px] rounded-full bg-[radial-gradient(circle,rgba(240,86,26,0.09),transparent_70%)] blur-3xl"></div>
      <div class="absolute left-1/2 top-[82%] h-[560px] w-[560px] -translate-x-1/2 rounded-full bg-[radial-gradient(circle,rgba(240,86,26,0.06),transparent_70%)] blur-3xl"></div>
    </div>
    <main class="relative z-10">
      <!-- ─── Hero ─────────────────────────────────────────────── -->
      <section class="relative overflow-hidden">
        <!-- Spotlight + grid backdrop -->
        <div
          class="pointer-events-none absolute inset-0"
          style="background:
            radial-gradient(44% 56% at 50% 40%, rgba(240,86,26,0.5) 0%, rgba(240,86,26,0.16) 42%, transparent 68%),
            radial-gradient(22% 30% at 50% 36%, rgba(255,146,66,0.45) 0%, transparent 60%),
            linear-gradient(100deg, rgba(58,22,10,0.7) 0%, transparent 50%, rgba(26,36,20,0.5) 100%);"
        ></div>
        <div class="bg-grid pointer-events-none absolute inset-0 opacity-25"></div>

        <SiteHeader />

        <!-- ── Desktop stage: centered mascot on the orange curtain, heading left ── -->
        <div class="relative z-10 mx-auto hidden max-w-[1200px] px-5 md:block">
          <div class="relative h-[600px]">
            <!-- Orange curtain backdrop (Figma "Rectangle 1" 2:1511) -->
            <svg
              class="pointer-events-none absolute left-1/2 top-[-72px] h-[730px] w-auto -translate-x-1/2"
              viewBox="0 0 478 973" preserveAspectRatio="xMidYMid meet" fill="none" xmlns="http://www.w3.org/2000/svg"
            >
              <path d="M0 0H478L428.315 105.094C385.086 196.532 357.717 294.655 347.376 395.267C341.142 455.929 341.142 517.071 347.376 577.733C357.717 678.345 385.086 776.468 428.315 867.906L478 973H0L49.6846 867.906C92.9136 776.468 120.283 678.345 130.624 577.733C136.858 517.071 136.858 455.929 130.624 395.267C120.283 294.655 92.9136 196.532 49.6846 105.094L0 0Z" fill="url(#heroCurtain)" />
              <defs>
                <linearGradient id="heroCurtain" x1="239" y1="116" x2="239" y2="1020.5" gradientUnits="userSpaceOnUse">
                  <stop stop-color="#C94306" /><stop offset="0.34" stop-color="#CE6311" /><stop offset="1" stop-color="#C94306" />
                </linearGradient>
              </defs>
            </svg>

            <!-- Mascot, centered; legs tuck behind the carousel below -->
            <img
              :src="mascot"
              alt="PlayStock mascot"
              class="mascot mascot--desktop pointer-events-none absolute bottom-[-44px] left-1/2 z-10 h-[600px] w-auto"
            />

            <!-- Heading on the left — the mascot's finger points at "Gamer's" -->
            <div class="absolute left-0 top-1/2 z-20 -translate-y-1/2">
              <h1 class="font-display text-5xl uppercase leading-[1.18] text-white lg:text-[60px]">
                {{ $t('home.heroLine1') }}<br /><span class="text-accent">{{ $t('home.heroLine2') }}</span><br />{{ $t('home.heroLine3') }}
              </h1>
              <router-link
                to="/catalog"
                class="mt-8 inline-flex rounded-full bg-accent px-8 py-3 text-sm font-semibold text-white
                       transition hover:bg-accent-2 hover:shadow-[0_0_30px_-6px_rgba(240,86,26,0.8)]"
              >{{ $t('home.browseCatalog') }}</router-link>
            </div>
          </div>
        </div>

        <!-- ── Mobile: title on top, large mascot centered, CTA overlapping the feet ── -->
        <div class="relative z-10 mx-auto max-w-[480px] px-6 pt-4 text-center md:hidden">
          <h1 class="font-display text-4xl uppercase leading-[1.18] text-white sm:text-5xl">
            {{ $t('home.heroLine1') }}<br /><span class="text-accent">{{ $t('home.heroLine2') }}</span><br />{{ $t('home.heroLine3') }}
          </h1>
          <div class="relative mt-2">
            <img :src="mascot" alt="PlayStock mascot" class="mascot mascot--mobile mx-auto w-[74%] max-w-[300px]" />
            <router-link
              to="/catalog"
              class="absolute bottom-3 left-1/2 w-[64%] max-w-[260px] -translate-x-1/2 rounded-full bg-accent
                     py-3.5 text-center text-sm font-semibold text-white shadow-[0_10px_30px_-8px_rgba(240,86,26,0.7)]
                     transition hover:bg-accent-2"
            >{{ $t('home.browseCatalog') }}</router-link>
          </div>
        </div>

        <!-- Coverflow carousel — overlaps the mascot's legs on desktop -->
        <div class="relative z-30 mx-auto max-w-[1200px] px-5 pb-10 pt-4 md:-mt-12 md:pt-0">
          <HeroCarousel v-if="carouselItems.length" :items="carouselItems" />
          <div v-else-if="loading" class="flex justify-center gap-4 py-8">
            <div v-for="i in 5" :key="i" class="h-64 w-44 animate-pulse rounded-2xl border border-line bg-surface"></div>
          </div>
        </div>
      </section>

      <!-- ─── Featured this week ──────────────────────────────────── -->
      <section id="featured" class="py-12 sm:py-16">
        <div class="mx-auto max-w-[1200px] px-5">
        <div class="mb-8 flex items-center justify-between">
          <h2 class="font-display text-2xl uppercase text-white sm:text-3xl">{{ $t('home.featuredTitle') }}</h2>
          <router-link to="/catalog" class="rounded-full bg-accent px-5 py-2 text-sm font-semibold text-white transition hover:bg-accent-2">{{ $t('home.viewMore') }}</router-link>
        </div>

        <div v-if="loading" class="grid grid-cols-2 gap-5 md:grid-cols-3 lg:grid-cols-4">
          <div v-for="i in 8" :key="i" class="aspect-[3/4] animate-pulse rounded-2xl border border-line bg-surface"></div>
        </div>

        <div v-else-if="featured.length" class="grid grid-cols-2 gap-4 sm:gap-5 md:grid-cols-3 lg:grid-cols-4">
          <router-link v-for="product in featured" :key="product.id" :to="`/product/${product.id}`" class="block">
            <CatalogCard :product="product" />
          </router-link>
        </div>

        <div v-else class="rounded-2xl border border-dashed border-line bg-surface px-6 py-16 text-center">
          <p class="font-display text-lg uppercase text-white">{{ $t('home.emptyTitle') }}</p>
          <p class="mx-auto mt-3 max-w-md text-sm leading-relaxed text-muted">
            <template v-if="failed">{{ $t('home.emptyFailed') }}</template>
            <template v-else>{{ $t('home.emptyBefore') }}<span class="text-accent">{{ $t('home.emptyKey') }}</span>{{ $t('home.emptyAfter') }}<code class="text-ink">.env</code>{{ $t('home.emptyAfter2') }}</template>
          </p>
        </div>
        </div>
      </section>

      <!-- ─── Platforms ───────────────────────────────────────────── -->
      <section class="py-12 sm:py-16">
        <div class="mx-auto max-w-[1200px] px-5">
          <div class="mb-8 flex items-center justify-between">
            <h2 class="font-display text-2xl uppercase text-white sm:text-3xl">{{ $t('home.platformsTitle') }}</h2>
            <router-link to="/catalog" class="rounded-full bg-accent px-5 py-2 text-sm font-semibold text-white transition hover:bg-accent-2">{{ $t('home.viewMore') }}</router-link>
          </div>
          <div class="grid grid-cols-2 gap-5 lg:grid-cols-4">
            <router-link
              v-for="platform in platforms"
              :key="platform.name"
              :to="{ path: '/catalog', query: { platform: platform.name } }"
              class="group block cursor-pointer"
            >
              <div class="platform-tile overflow-hidden border border-line bg-surface transition group-hover:border-accent">
                <img
                  :src="platform.art"
                  :alt="platform.name"
                  loading="lazy"
                  class="aspect-[3/4] w-full object-cover transition duration-500 group-hover:scale-105"
                />
              </div>
              <p class="mt-3 text-center font-display text-sm uppercase tracking-wide text-white transition group-hover:text-accent">{{ platform.name }}</p>
            </router-link>
          </div>
        </div>
      </section>

      <!-- ─── How PlayStock works ─────────────────────────────────── -->
      <section class="py-12 sm:py-16">
        <div class="mx-auto max-w-[1200px] px-5">
        <h2 class="mb-10 font-display text-2xl uppercase text-white sm:text-3xl">{{ $t('home.howTitle') }}</h2>
        <div class="grid gap-6 md:grid-cols-3">
          <div
            v-for="step in steps"
            :key="step.title"
            class="group rounded-2xl border border-line bg-surface p-7 transition hover:border-accent hover:bg-accent/5 hover:shadow-[0_0_40px_-12px_rgba(240,86,26,0.6)]"
          >
            <span
              class="grid h-14 w-14 place-items-center rounded-full border border-accent/60 text-accent transition group-hover:border-transparent group-hover:bg-accent group-hover:text-white"
            >
              <svg v-if="step.icon === 'controller'" width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"><path d="M6 12h4M8 10v4" /><circle cx="15.5" cy="11" r="0.6" fill="currentColor" stroke="none" /><circle cx="17.5" cy="13" r="0.6" fill="currentColor" stroke="none" /><path d="M17.5 6H6.5A4.5 4.5 0 0 0 2 10.5L3.2 17a2 2 0 0 0 3.6.8L8 16h8l1.2 1.8a2 2 0 0 0 3.6-.8L22 10.5A4.5 4.5 0 0 0 17.5 6Z" /></svg>
              <svg v-else-if="step.icon === 'wallet'" width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="6" width="18" height="13" rx="2" /><path d="M3 10h18" /><path d="M16 14h2" /></svg>
              <svg v-else width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"><circle cx="8" cy="8" r="4" /><path d="m11 11 8 8M16 16l2-2M19 19l2-2" /></svg>
            </span>
            <h3 class="mt-6 font-display text-lg uppercase leading-tight text-white transition group-hover:text-accent">{{ step.title }}</h3>
            <p class="mt-3 text-sm leading-relaxed text-muted">{{ step.body }}</p>
          </div>
        </div>
        </div>
      </section>
    </main>

    <SiteFooter />
  </div>
</template>

<style scoped>
/* Diagonally-cut corners (top-left + bottom-right), matching the Figma
   platform tiles. */
.platform-tile {
  clip-path: polygon(
    30px 0,
    100% 0,
    100% calc(100% - 30px),
    calc(100% - 30px) 100%,
    0 100%,
    0 30px
  );
}

/* Hero mascot: gentle weightless "levitation" bob + a slow pulsing accent glow,
   so the white character feels alive without being distracting. */
.mascot {
  will-change: transform, filter;
}
.mascot--desktop {
  /* keep the translateX(-50%) centering inside the keyframes */
  animation: mascot-float-x 6s ease-in-out infinite, mascot-glow 4.5s ease-in-out infinite;
}
.mascot--mobile {
  animation: mascot-float 6s ease-in-out infinite, mascot-glow 4.5s ease-in-out infinite;
}

@keyframes mascot-float-x {
  0%, 100% { transform: translateX(-50%) translateY(0) rotate(0deg); }
  50%      { transform: translateX(-50%) translateY(-16px) rotate(-1.2deg); }
}
@keyframes mascot-float {
  0%, 100% { transform: translateY(0) rotate(0deg); }
  50%      { transform: translateY(-12px) rotate(-1.2deg); }
}
@keyframes mascot-glow {
  0%, 100% { filter: drop-shadow(0 22px 30px rgba(0, 0, 0, 0.5)) drop-shadow(0 0 0 rgba(240, 86, 26, 0)); }
  50%      { filter: drop-shadow(0 22px 30px rgba(0, 0, 0, 0.5)) drop-shadow(0 0 30px rgba(240, 86, 26, 0.55)); }
}

@media (prefers-reduced-motion: reduce) {
  .mascot {
    animation: none;
    filter: drop-shadow(0 22px 30px rgba(0, 0, 0, 0.5));
  }
}
</style>
